// ============================================================================
// The open termination, and the four things you can do to it.
//
// This is what replaced account-termination.tsx on 2026-08-21.
//
// Ben: "Stop Service replaced account termination, so remove the account
// termination portion in the Actions tab. Unless there is still something
// there that was not transferred over." There was — Stop Service had replaced
// the CREATING half and none of the after half. Five controls and the record
// itself lived only in that card, and deleting it would have deleted the only
// way to acknowledge anything.
//
// So this file is that half, rewritten native to Stop Service rather than
// embedded as a foreign card. It renders NOTHING unless there is a termination
// to show.
//
// WHAT DELIBERATELY DID NOT COME ACROSS
//   * The create form. Stop Service creates, by asking the right question
//     ("this facility, pause or terminate") instead of the wrong one ("which
//     account").
//   * The scope editor.
//   * The account-wide "close every facility on this payer" button. Ben's
//     decision, 2026-08-21: used ZERO times in five termination records, and a
//     close-everything control on the screen built to stop conflating accounts
//     with facilities is the exact confusion it exists to end. The DATABASE
//     still supports scope='account' (TCSD-0081) — it simply has no button.
//     A record that IS account-scoped still renders here correctly.
// ============================================================================
import { canAckTerminationForFinance, canAckTerminationForHr, canGiveFinalTerminationApproval, canOpenTermination } from '../../shared/role-gates';
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../shared/supabase';
import { useAuth } from '../../shared/auth';
import { useToast } from '../../shared/components';
import { TerminationRecordView, coversFacilityFilter, fieldErrorFrom } from './stop-service-parts';
// The step itself, shared with Ask AI's building card (item 944).
import { terminationStep } from './lib/building-saves';
import type { TerminationRecordRow, TerminationScopeFacility } from './stop-service-parts';

// Mirrors the RPCs' own checks so a control is absent rather than a surprise.
// The RPCs remain the authority.

// The row as READ, which is the view's row plus the two columns only the
// loader needs: the account it hangs off, and the reason code it resolves to a
// label. The view never sees either.
interface TerminationRow extends TerminationRecordRow {
  account_id: string;
  end_reason_code: string | null;
}

type ScopeFacility = TerminationScopeFacility;

export function TerminationRecord({
  accountId,
  facilityId,
  accountName,
  onChanged,
}: {
  accountId: string | null;
  /**
   * The facility whose page this is. The panel shows the termination covering
   * THIS facility — since 2026-10-13 an account can carry one per facility, so
   * "the account's latest" would show a sibling's record (and its date).
   */
  facilityId: string | null;
  accountName: string | null;
  /** Called after any write, so the page can re-read the facility. */
  onChanged?: () => void;
}) {
  const { profile } = useAuth();
  const role = profile?.role;
  const toast = useToast();

  const [row, setRow] = useState<TerminationRow | null>(null);
  const [facilities, setFacilities] = useState<ScopeFacility[]>([]);
  const [reasonLabel, setReasonLabel] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!accountId || !facilityId) {
      setRow(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    // Latest non-cancelled record COVERING THIS FACILITY. Completed is
    // INCLUDED on purpose: it is the audit trail of what closed, when, and who
    // signed it off, and it carries the planned final service date the dates
    // row pairs with the actual close.
    const { data } = await supabase
      .from('account_terminations')
      .select('*')
      .eq('account_id', accountId)
      .or(coversFacilityFilter(facilityId))
      .not('status', 'in', '("Cancelled")')
      .order('created_at', { ascending: false })
      .limit(1);
    const r = ((data ?? []) as TerminationRow[])[0] ?? null;
    setRow(r);

    if (r) {
      // Flat fetch + IN-lookup + client-join, the house pattern. The facility
      // list is read from `facilities` rather than from the termination's own
      // array so a site that has since closed still resolves to a name.
      const ids = r.scope === 'account' ? null : (r.facility_ids ?? []);
      const [facs, reason] = await Promise.all([
        ids && ids.length > 0
          ? supabase
              .from('facilities')
              .select('facility_id, facility_name, monthly_rate')
              .in('facility_id', ids)
              .order('facility_name', { ascending: true })
          : ids === null
            ? supabase
                .from('facilities')
                .select('facility_id, facility_name, monthly_rate')
                .eq('account_id', accountId)
                .not('status', 'in', '("Closed","Merged")')
                .order('facility_name', { ascending: true })
            : Promise.resolve({ data: [], error: null }),
        r.end_reason_code
          ? supabase
              .from('facility_lifecycle_reasons')
              .select('label')
              .eq('reason_code', r.end_reason_code)
              .eq('kind', 'end')
              .maybeSingle<{ label: string }>()
          : Promise.resolve({ data: null, error: null }),
      ]);
      setFacilities((facs.data ?? []) as ScopeFacility[]);
      setReasonLabel(reason.data?.label ?? r.end_reason_code ?? null);
    }
    setLoading(false);
  }, [accountId, facilityId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function callRpc(
    fn:
      | 'rpc_ack_account_termination'
      | 'rpc_set_account_termination_final_date'
      | 'rpc_cancel_account_termination',
    args: Record<string, unknown>,
    okMsg: string,
  ) {
    setBusy(true);
    try {
      await terminationStep(fn, args);
      toast.success(okMsg);
      await load();
      onChanged?.();
    } catch (e) {
      // The RPCs put a finished human sentence in MESSAGE; show it verbatim
      // rather than inventing one (PLAYBOOK §11).
      const { message } = fieldErrorFrom(e, 'Could not save. Please try again.');
      toast.error('Could not save', { description: message, duration: 0 });
    } finally {
      setBusy(false);
    }
  }

  // Nothing to manage, nothing rendered. Stop Service already offers the
  // create path on the same screen; a second affordance here would be the
  // two-identical-pages problem this whole run exists to end.
  if (loading || !accountId || !row) return null;

  const canFinance = canAckTerminationForFinance(profile);
  const canHr = canAckTerminationForHr(role);
  const canApprove = canGiveFinalTerminationApproval(role);
  const canCancel = canOpenTermination(role);

  return (
    <TerminationRecordView
      row={row}
      facilities={facilities}
      payerLabel={accountName ?? accountId}
      reasonLabel={reasonLabel}
      busy={busy}
      canFinance={canFinance}
      canHr={canHr}
      canApprove={canApprove}
      canCancel={canCancel}
      onAck={(step) =>
        void callRpc(
          'rpc_ack_account_termination',
          { p_termination_id: row.termination_id, p_step: step },
          step === 'approve'
            ? 'Termination approved'
            : step === 'finance'
              ? 'Finance acknowledged'
              : 'HR acknowledged',
        )
      }
      onSetFinalDate={(d) =>
        void callRpc(
          'rpc_set_account_termination_final_date',
          { p_termination_id: row.termination_id, p_final_service_date: d },
          'Final service date set',
        )
      }
      onCancel={() => {
        // The view only renders this control while `applied_at` is null, so the
        // promise below is true whenever it can be read (TCSD-0273). Once the
        // close has run, cancelling would not reopen anything and the RPC
        // refuses it — so the button is absent rather than a dead end.
        if (
          window.confirm(
            `Cancel termination ${row.termination_id}? Nothing closes, and the facilities stay in service.`,
          )
        ) {
          void callRpc(
            'rpc_cancel_account_termination',
            { p_termination_id: row.termination_id, p_reason: null },
            'Termination cancelled — nothing will close',
          );
        }
      }}
    />
  );
}
